import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { getReq, putReq } from "../../configs/ApiUrl";
import { useTranslation } from "react-i18next";
import AlwaysTop from "./util/AlwaysTop";
import { LoadingSpinner } from "./util/LoadingSpinner";
import moment from "moment";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Chip,
  useMediaQuery,
  Tooltip
} from "@mui/material";
import {
  CheckCircle,
  Close,
  ListAltRounded,
  LocalShipping,
  Visibility,
} from "@mui/icons-material";
import Button from "../Button";
import { motion } from "framer-motion";
import DataTable from "../DataTable.js";

function ActiveOrders() {
  const { t } = useTranslation(["admin", "altText", "tooltips"]);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:600px)");

  const [loading, setLoading] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [subadminEmail, setSubadminEmail] = useState("");
  const [courier, setCourier] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const scrollToTop = () => {
    if (containerRef.current) {
      window.scrollTo({
        top: containerRef.current.offsetTop - 200,
        behavior: "smooth",
      });
    }
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    setTimeout(scrollToTop, 150);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
    setTimeout(scrollToTop, 150);
  };

  useEffect(() => {
    fetchUserRole();
    AlwaysTop();
  }, []);

  useEffect(() => {
    if (userRole) getAllOrders();
  }, [userRole]);

  const fetchUserRole = async () => {
    const profile = await getReq("/user/profile");
    const userId = profile?.user?._id;
    const email = profile?.user?.email;
    const roleResp = await getReq(`/user/${userId}`);
    if (roleResp?.success) {
      setUserRole(roleResp.role);
      if (roleResp.role === "subadmin") {
        setSubadminEmail(email);
        setCourier(roleResp.companyName);
      }
    }
  };

  const getAllOrders = async () => {
    setLoading(true);
    const res = await getReq("/order/all");
    if (res?.success) {
      const filtered = res.allOrders.filter((o) => {
        return userRole === "admin"
          ? ["placed", "paid"].includes(o.orderStatus)
          : [t("admin:placedLabel"), t("admin:paidLabel")].includes(o.orderStatus) &&
              o.subadminEmail === subadminEmail;
      });
      setAllOrders(filtered);
    }
    setLoading(false);
  };

  const openPaymentModal = (order) => {
    setSelectedOrder(order);
    setShowPaymentModal(true);
  };

  const openDetailsModal = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const openDeliveryModal = (order) => {
    setSelectedOrder(order);
    setShowDeliveryModal(true);
  };

  const closeModals = () => {
    setSelectedOrder(null);
    setShowPaymentModal(false);
    setShowDetailsModal(false);
    setShowDeliveryModal(false);
  };

  const handleConfirmPayment = async (order) => {
    setLoading(true);
    const res = await putReq(`/order/${order._id}/update-status`, {
      status: "paid",
    });
    res?.success
      ? message.success("Payment Confirmed")
      : message.error("Failed");
    getAllOrders();
    setLoading(false);
    closeModals();
  };

  const handleDeclinePayment = async (order) => {
    setLoading(true);
    const res = await putReq(`/order/${order._id}/update-status`, {
      status: "cancelled",
    });
    res?.success ? message.warning("Order Cancelled") : message.error("Failed");
    getAllOrders();
    setLoading(false);
    closeModals();
  };

  const handleConfirmDelivery = async (order) => {
    setLoading(true);
    const res = await putReq(`/order/${order._id}/update-status`, {
      status: "delivered",
    });
    res?.success
      ? message.success("Delivery Confirmed")
      : message.error("Failed");
    getAllOrders();
    setLoading(false);
    closeModals();
  };

  const handleCancelDelivery = async (order) => {
    setLoading(true);
    const item = order.orderItems?.[0];
    const actualProduct = item?.product || item?.productId;
    const reqData = {
      order_id: order?._id,
      id: actualProduct?._id,
      status: order?.subadminEmail ? "approved" : "preapproved",
      customerId: order?.customerId,
      deliverySlots: order.deliverySlots,
    };
    const res = await putReq("/product/decline/payment", reqData);
    if (res?.success) {
      await putReq(`/order/${order._id}/update-status`, {
        status: "cancelled",
      });
      message.warning("Order Cancelled");
    } else message.error("Failed");
    getAllOrders();
    setLoading(false);
    closeModals();
  };

  const columns = [
    {
      header: t("tables:product"),
      accessor: (row) => row.orderItems?.[0]?.product?.title || row.orderItems?.[0]?.productId?.title || "-",
      mobile: true,
      bold: true,
    },
    {
      header: t("tables:price"),
      accessor: (row) => `${row.orderItems?.[0]?.product?.price || row.orderItems?.[0]?.productId?.price || 0} kr`,
      mobile: true,
    },
    {
      header: t("tables:status"),
      accessor: (row) => (
        <Chip
          label={row.orderStatus.charAt(0).toUpperCase() + row.orderStatus.slice(1)}
          color={
            row.orderStatus === "placed"
              ? "warning"
              : row.orderStatus === "paid"
              ? "success"
              : "default"
          }
        />
      ),
      mobile: true,
    },
  ];

  return (
    <motion.div
      ref={containerRef}
      className="w-full px-2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Box className="w-full px-2 pb-4">
        {loading ? (
          <LoadingSpinner />
        ) : allOrders.length === 0 ? (
          <Typography
            variant="h6"
            className="text-center text-gray-500 mt-10"
          >
            {t("admin:noOrders")}
          </Typography>
        ) : (
          <DataTable
            columns={columns}
            data={allOrders}
            getImage={(row) => row.orderItems?.[0]?.product?.mainImage || row.orderItems?.[0]?.productId?.mainImage || ""}
            getRowKey={(row) => row._id}
            renderRowActions={(order) => (
              <>
                <Tooltip title={t("tooltips:viewDetails")}>
                  <IconButton onClick={() => openDetailsModal(order)}>
                    <Visibility />
                  </IconButton>
                </Tooltip>
                {order.orderStatus === "placed" && (
                  <Tooltip title={t("tooltips:finalisePayment")}>
                    <IconButton onClick={() => openPaymentModal(order)}>
                      <ListAltRounded />
                    </IconButton>
                  </Tooltip>
                )}
                {order.orderStatus === "paid" && (
                  <Tooltip title={t("tooltips:manageDelivery")}>
                    <IconButton onClick={() => openDeliveryModal(order)}>
                      <LocalShipping />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            emptyMessage={t("admin:noOrders")}
            page={page}
            rowsPerPage={rowsPerPage}
            handlePageChange={handleChangePage}
            handleRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}

        {selectedOrder && showPaymentModal && (
          <Modal
            open={showPaymentModal}
            onClose={closeModals}
            className="flex justify-center items-center"
          >
            <Box className="bg-white p-8 rounded-lg shadow-lg w-[350px] text-center space-y-4">
              <Typography variant="h6" className="font-bold">
                Payment Confirmation
              </Typography>
              <Typography className="text-gray-600">
                Payment Method: {selectedOrder.paymentMethod}
              </Typography>
              <Typography className="text-gray-600">
                Amount: {selectedOrder.orderItems[0]?.grandTotal} kr
              </Typography>
              <Button
                variant="confirm"
                startIcon={<CheckCircle />}
                onClick={() => handleConfirmPayment(selectedOrder)}
              >
                Confirm Payment
              </Button>
              <Button
                variant="danger"
                startIcon={<Close />}
                onClick={() => handleDeclinePayment(selectedOrder)}
              >
                Decline Payment
              </Button>
            </Box>
          </Modal>
        )}

        {selectedOrder && showDetailsModal && (
          <Modal
            open={showDetailsModal}
            onClose={closeModals}
            className="flex justify-center items-center"
          >
            <Box className="relative bg-white p-8 rounded-lg shadow-lg w-[450px] text-center space-y-6">
              <button
                onClick={closeModals}
                className="absolute top-2 right-3 text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                &times;
              </button>
              <Typography variant="h5" className="font-bold text-gray-800">
                Order Details
              </Typography>
              <Box className="text-left">
                <Typography variant="h6" className="font-semibold text-gray-700">
                  Order ID:
                </Typography>
                <Typography className="text-gray-600">
                  {selectedOrder.orderNo}
                </Typography>
              </Box>
              <Box className="text-left">
                <Typography variant="h6" className="font-semibold text-gray-700">
                  Shipping Address:
                </Typography>
                {selectedOrder.shippingAddress ? (
                  <Typography className="text-gray-600">
                    {selectedOrder.shippingAddress.street}, {selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.state}
                  </Typography>
                ) : (
                  <Typography className="text-gray-500">
                    No shipping address available
                  </Typography>
                )}
              </Box>
              <Box className="text-left">
                <Typography variant="h6" className="font-semibold text-gray-700">
                  Delivery Slots:
                </Typography>
                {selectedOrder.deliverySlots?.length > 0 ? (
                  selectedOrder.deliverySlots.map((slot, index) => (
                    <Typography key={index} className="text-gray-600">
                      {moment(slot.date).format("DD/MM/YYYY")} - {slot.time}
                    </Typography>
                  ))
                ) : (
                  <Typography className="text-gray-500">
                    No delivery slots assigned
                  </Typography>
                )}
              </Box>
            </Box>
          </Modal>
        )}

        {selectedOrder && showDeliveryModal && (
          <Modal
            open={showDeliveryModal}
            onClose={closeModals}
            className="flex justify-center items-center"
          >
            <Box className="relative bg-white p-8 rounded-lg shadow-lg w-[350px] text-center space-y-4">
              <button
                onClick={closeModals}
                className="absolute top-2 right-3 text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                &times;
              </button>
              <Typography variant="h6" className="font-bold">
                Manage Delivery
              </Typography>
              <Typography className="text-gray-600">
                Order: {selectedOrder.orderNo}
              </Typography>
              <Typography className="text-gray-600">
                Please choose an action:
              </Typography>
              <Button
                variant="confirm"
                startIcon={<CheckCircle />}
                onClick={() => handleConfirmDelivery(selectedOrder)}
              >
                Confirm Delivery
              </Button>
              <Button
                variant="danger"
                startIcon={<Close />}
                onClick={() => handleCancelDelivery(selectedOrder)}
              >
                Cancel Order
              </Button>
            </Box>
          </Modal>
        )}
      </Box>
    </motion.div>
  );
}

export default ActiveOrders;